import type { DsiSourceInfo } from './live-preview';
import { GAME_TYPE_ID, gameListLabel, isOfferedGameList, type SiteSourceContent } from './site-games';
import type { SitePageList } from './site-pages-api';

/**
 * The names a site page gives its game lists. On spark sites a casino page (Home, Casino, ...) is built from
 * one `dynamic-page-element` data source whose items are its sections, and a section that shows a game list
 * carries the title the site renders above it: `gamesCarouselProps` or `gamesGridListerProps`, shaped
 * `{ title, dsId: { ref_id } }`. The same list can be shown under different titles on different pages
 * (`spk-crash-games` is "Kaboom Games" on one Home and "Crash games" on a Casino page), so a list's name is
 * read per page. Pure helpers only.
 */

const SECTIONS_TYPE_ID = 'dynamic-page-element';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const nonEmpty = (value: unknown): string | null => (typeof value === 'string' && value.trim() ? value.trim() : null);

/** A title in `language`: a plain title as is, else that language, English, then any translation. */
const titleIn = (title: unknown, language: string): string | null => {
  if (!isRecord(title)) return nonEmpty(title);
  return (
    nonEmpty(title[language]) ??
    nonEmpty(title.en) ??
    Object.values(title)
      .map(nonEmpty)
      .find((value): value is string => value !== null) ??
    null
  );
};

/**
 * The page's section source among the sources the site reports: the one `named` (the service's word for the page)
 * once the site reports it, else, with no name, the first data source of type `dynamic-page-element`. A page can
 * load another page's sections too (a template's hidden node on a test page), and the site reports its sources in
 * the order they loaded, so the first is not necessarily the page's own. Null until it is reported.
 */
export const sectionSourceId = (sources: DsiSourceInfo[], named = ''): string | null => {
  if (named) return sources.some((s) => s.id === named) ? named : null;
  return sources.find((s) => s.typeId === SECTIONS_TYPE_ID)?.id ?? null;
};

/**
 * Each game list on the page mapped to the title its section shows, in `language`, as the site shows it (`shown`: the
 * site's own translation of a text, see `site-translations`). A list shown in several sections gets their titles in
 * page order, each once, joined with " / ". Sections without a list or a title add nothing.
 */
export const listTitles = (
  content: SiteSourceContent,
  language: string,
  shown: (text: string) => string = (text) => text,
): Record<string, string> => {
  const byList = new Map<string, string[]>();
  for (const row of content.data) {
    if (!isRecord(row)) continue;
    for (const props of Object.values(row)) {
      if (!isRecord(props) || !isRecord(props.dsId)) continue;
      const listId = nonEmpty(props.dsId.ref_id);
      const read = titleIn(props.title, language);
      const title = read === null ? null : shown(read);
      if (!listId || !title) continue;
      const titles = byList.get(listId) ?? [];
      if (!titles.includes(title)) byList.set(listId, [...titles, title]);
      break;
    }
  }
  return Object.fromEntries([...byList].map(([listId, titles]) => [listId, titles.join(' / ')]));
};

/** The list a section's props show: `dsId` as the site serves it (`{ ref_id }`) or as a plain id. */
const listIdOf = (props: Record<string, unknown>): string | null =>
  isRecord(props.dsId) ? nonEmpty(props.dsId.ref_id) : nonEmpty(props.dsId);

/** Every game list the page's sections show, titled or not, in page order. */
export const sectionListIds = (content: SiteSourceContent): string[] => {
  const ids: string[] = [];
  for (const row of content.data) {
    if (!isRecord(row)) continue;
    for (const props of Object.values(row)) {
      if (!isRecord(props)) continue;
      const listId = listIdOf(props);
      if (!listId) continue;
      if (!ids.includes(listId)) ids.push(listId);
      break;
    }
  }
  return ids;
};

/** The game lists among the lists the service read on the page (`SitePage.lists`), once each, in page order: the
 *  site's game lists (`slot_game`) the tab offers, so neither the whole catalogue nor a withheld list. A hidden or
 *  one-device section's list is one too, as the site fetches it. */
export const serviceListIds = (lists: readonly SitePageList[]): string[] => {
  const ids: string[] = [];
  for (const { listId, listType } of lists) {
    if (listType === GAME_TYPE_ID && isOfferedGameList(listId) && !ids.includes(listId)) ids.push(listId);
  }
  return ids;
};

/** Each list the service read on the page mapped to the title its section shows, by `listTitles`' rules: in
 *  `language`, then English, then any; as the site shows it (`shown`); a list two sections show gets their titles in
 *  page order, each once, joined with " / ". A section with no title adds nothing. */
export const serviceListTitles = (
  lists: readonly SitePageList[],
  language: string,
  shown: (text: string) => string = (text) => text,
): Record<string, string> => {
  const byList = new Map<string, string[]>();
  for (const list of lists) {
    const read = titleIn(list.title, language);
    const title = read === null ? null : shown(read);
    if (!title) continue;
    const titles = byList.get(list.listId) ?? [];
    if (!titles.includes(title)) byList.set(list.listId, [...titles, title]);
  }
  return Object.fromEntries([...byList].map(([listId, titles]) => [listId, titles.join(' / ')]));
};

/** What to call a list: the page's title for it, else a name read from its data-source id. */
export const listLabel = (listId: string, titles: Record<string, string>): string =>
  titles[listId] ?? gameListLabel(listId);
